import { Field, formatEuro, Input } from '@/design-system';
import { amountToCents, parseEuroToCents, URGENCY_LABELS, type Urgency } from '@/entities/finding';

import { surveyCopy } from '../copy';
import type { Answer } from '../engine';
import { useDraftText } from '../hooks/useDraftText';
import { hasOwnAmount } from '../model/view';

export interface AmountPromptFieldProps {
    /** De urgentie die de keuze noemt: daar komt het bedrag. */
    urgency: Urgency;
    /** Het antwoord, met de eigen bedragen van de inspecteur erop. */
    answer: Answer | undefined;
    questionLabel: string;
    readOnly?: boolean;
    /** Hele centen; `null` als het veld is leeggemaakt. */
    onCommit: (cents: number | null) => void;
}

/**
 * Het bedragveld direct onder een keuze zonder vast bedrag ("Gebreken > bedrag zelf
 * invullen"). De vragenlijst heeft daar bewust geen bedrag: het verschilt per woning
 * te veel. Het veld staat open, niet achter "Aanpassen", en zegt in amber dat het
 * nog leeg is - geen rood en geen blokkade: indienen kan altijd, het afrondscherm
 * noemt het. Wat hier staat is een eigen bedrag op het antwoord, precies als een
 * aangepast bedrag, dus het rapport en "Aanpassen" daar lezen het gewoon.
 */
export function AmountPromptField({
    urgency,
    answer,
    questionLabel,
    readOnly = false,
    onCommit,
}: AmountPromptFieldProps) {
    const copy = surveyCopy.amountPrompt;
    const filled = hasOwnAmount(answer);
    const cents =
        (amountToCents(answer?.direct_cost) ?? 0) +
        (amountToCents(answer?.term_cost) ?? 0) +
        (amountToCents(answer?.improvement_cost) ?? 0);
    const shown = filled ? formatEuro(Math.round(cents / 100)) : '';
    const [value, setValue] = useDraftText(shown);

    return (
        <div
            data-testid="amount-prompt"
            data-filled={filled ? 'true' : 'false'}
            className="flex flex-col gap-[6px] border-t border-[var(--border-subtle)] bg-[var(--surface-detail)] px-3 py-[10px]"
        >
            <p
                className={
                    filled
                        ? 'm-0 text-[12px] leading-[1.4] text-[var(--text-secondary)]'
                        : 'm-0 text-[13px] leading-[1.4] font-medium text-[var(--warn-text)]'
                }
            >
                {filled ? copy.filled : copy.title}
            </p>

            <Field
                label={copy.label(URGENCY_LABELS[urgency])}
                description={filled ? undefined : copy.explanation}
                className="max-w-[260px]"
            >
                <Input
                    mono
                    inputMode="numeric"
                    aria-label={copy.ariaLabel(questionLabel)}
                    placeholder={copy.placeholder}
                    value={value}
                    readOnly={readOnly}
                    onChange={(event) => setValue(event.target.value)}
                    onBlur={() => {
                        if (value !== shown) {
                            onCommit(parseEuroToCents(value));
                        }
                    }}
                />
            </Field>
        </div>
    );
}
